import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'
import { hasText } from '@payloadcms/richtext-lexical/shared'

interface LexicalNode {
  type: string
  text?: string
  children?: LexicalNode[]
}

/**
 * A Lexical-valid empty document. `setEditorState` throws if the root has no
 * children, so optional rich-text fields must never persist `{ children: [] }`.
 */
export const EMPTY_LEXICAL_EDITOR_STATE: DefaultTypedEditorState = {
  root: {
    type: 'root',
    children: [
      {
        type: 'paragraph',
        children: [],
        direction: null,
        format: '',
        indent: 0,
        textFormat: 0,
        version: 1,
      },
    ],
    direction: null,
    format: '',
    indent: 0,
    version: 1,
  },
}

/**
 * Recursively checks whether a serialized Lexical node tree contains any
 * meaningful content (text, images, embeds, etc.).
 *
 * Payload's `hasText` only recognises standard `paragraph` nodes, so this
 * covers custom node types like `custom-paragraph` that the CMS may produce
 * for empty rich text fields.
 */
function nodeHasContent(node: LexicalNode): boolean {
  if (typeof node.text === 'string' && node.text.length > 0) {
    return true
  }

  const nonTextContentTypes = ['upload', 'block', 'horizontalrule', 'table']
  if (nonTextContentTypes.includes(node.type)) {
    return true
  }

  if (Array.isArray(node.children)) {
    return node.children.some(nodeHasContent)
  }

  return false
}

/**
 * Returns `true` if the given Lexical rich text state contains any visible
 * content. Returns `false` for `null`, `undefined`, or structurally empty
 * documents (e.g. a single empty paragraph node with no text children).
 *
 * Uses Payload's built-in `hasText` as a fast first pass, then falls back to
 * a recursive check that also handles custom node types.
 */
export function hasRichTextContent(value: DefaultTypedEditorState | null | undefined): boolean {
  if (!hasText(value)) return false

  const children = (value?.root as LexicalNode | undefined)?.children
  if (!children?.length) return false
  return children.some(nodeHasContent)
}

function isLexicalEditorStateObject(
  value: unknown,
): value is DefaultTypedEditorState & { root?: LexicalNode } {
  return typeof value === 'object' && value !== null && 'root' in value
}

/**
 * True when serialized state cannot be passed to Lexical `setEditorState`
 * because the root is missing or has no children.
 */
export function isUnloadableLexicalEditorState(value: unknown): boolean {
  if (!isLexicalEditorStateObject(value)) return false
  const children = value.root?.children
  return !Array.isArray(children) || children.length === 0
}

/**
 * Makes stored Lexical JSON safe to load in the admin editor.
 *
 * `null` / `undefined` are left unchanged so readers (including Patient Portal)
 * still treat a missing field as empty. Only unloadable objects
 * (`{ root: { children: [] } }`) are rewritten: optional fields become `null`,
 * required fields become a valid empty paragraph so Lexical can remount.
 */
export function sanitizeLexicalEditorState(
  value: unknown,
  options?: { required?: boolean },
): DefaultTypedEditorState | null | undefined {
  if (value == null) {
    return value
  }
  if (isUnloadableLexicalEditorState(value)) {
    return options?.required ? EMPTY_LEXICAL_EDITOR_STATE : null
  }
  return value as DefaultTypedEditorState
}
